import { useState } from "react";
import styled from "styled-components";

export const ClientInfoBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.clientInfo.textColor};
  gap: 10px;
  font-weight: bold;
  background-color: ${({ theme }) => theme.clientInfo.backgroundColor};
  padding: 10px 20px;
  min-width: 230px;
  max-width: 260px;
  min-height: 70px;

  height: auto;
  padding: 8px 16px 8px 8px;
  gap: 16px;
  border-radius: 24px;
  min-width: 0;
  max-width: none;
  height: 72px;
  min-height: 72px;
  font-weight: 500;
  background-color: #ffffff;
  color: #23231f;
  font-size: 2rem;
  @media screen and (max-width: 480px) {
    height: 56px;
    min-height: 56px;
    font-size: 2rem;
  }
`;

export const ClientLogo = styled.div<{ $variant: "image" | "monogram" }>`
  width: 56px;
  height: 56px;
  min-width: 56px;
  min-height: 56px;
  max-width: 56px;
  max-height: 56px;
  flex: 0 0 56px;
  /* Logos are letterboxed on white; the monogram keeps the brand tile. */
  background: ${({ $variant }) =>
    $variant === "monogram" ? "#371f13" : "#ffffff"};
  border-radius: 16px;
  padding: 0;
  overflow: hidden;
  outline: 1px solid #ffffff;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  @media screen and (max-width: 480px) {
    width: 40px;
    height: 40px;
    min-width: 40px;
    min-height: 40px;
    max-width: 40px;
    max-height: 40px;
    flex: 0 0 40px;
    border-radius: 12px;
  }
`;

export const ClientImg = styled.img`
  width: 100%;
  height: 100%;
  display: block;
  /* contain, not cover: wordmark logos are far wider than the square tile. */
  object-fit: contain;
`;

const ClientMonogram = styled.span`
  font-family: ${({ theme }) => theme.headingFontFamily};
  /* Only HeadlandOne-400 is loaded; any other weight would be synthesised. */
  font-weight: 400;
  /* 65.6% of the tile, which puts the cap height at 50% of it — the same
     proportion as the C in cleverbase-app-icon.png. Keep both in step with
     the ClientLogo tile sizes above (56px / 40px). */
  font-size: 3.68rem;
  line-height: 1;
  color: #faf5eb;
  user-select: none;
  @media screen and (max-width: 480px) {
    font-size: 2.63rem;
  }
`;

function monogramOf(clientName: string): string {
  return (Array.from(clientName.trim())[0] ?? "").toUpperCase();
}

export function ClientLogoTile({
  clientName,
  clientLogo,
}: {
  clientName: string;
  clientLogo: string | undefined;
}): JSX.Element {
  // Tracks the src rather than a flag, so a new logo gets a fresh attempt.
  const [failedLogo, setFailedLogo] = useState<string | undefined>(undefined);
  const logo =
    !!clientLogo && clientLogo !== failedLogo ? clientLogo : undefined;

  return (
    <ClientLogo
      data-testid="client_logo"
      $variant={!!logo ? "image" : "monogram"}
    >
      {!!logo ? (
        <ClientImg src={logo} alt="" onError={() => setFailedLogo(logo)} />
      ) : (
        <ClientMonogram aria-hidden="true">
          {monogramOf(clientName)}
        </ClientMonogram>
      )}
    </ClientLogo>
  );
}

const ClientName = styled.div`
  font-size: 2rem;
  font-weight: 500;
  line-height: 2.8rem;
`;

export interface ClientNameBoxProps {
  clientName: string;
  clientLogo: string | undefined;
  display: boolean;
}

export default function ClientNameBox({
  clientName,
  clientLogo,
  display,
}: ClientNameBoxProps): JSX.Element {
  return (
    <>
      {!!clientName && display && (
        <ClientInfoBox>
          <ClientLogoTile clientName={clientName} clientLogo={clientLogo} />
          <ClientName data-testid="client_name">{clientName}</ClientName>
        </ClientInfoBox>
      )}
    </>
  );
}
